<template>
  <collapsible-widget
    title="WARSTWY"
    :accordion="accordion"
    :default-expanded="defaultExpanded"
    :expand-widget="expandWidget"
    @toggled="onToggled"
  >
    <h4 class="p-3 text-center" v-if="!layers.length">
      Trwa ładowanie danych...
    </h4>
    <div v-if="layers.length" id="layers-container" style="flex: 1;">
      <b-list-group id="layers-list">
        <b-list-group-item
          v-for="item in layers"
          :key="item.id"
          class="layers-list-group-item"
        >
          <layer
            :layer="item"
            :isFirst="isFirstLayer(item)"
            :isLast="isLastLayer(item)"
            @move-down="moveLayerDown(item)"
            @move-up="moveLayerUp(item)"
            @activate="toggleActive(item)"
            @toggle-visible="toggleVisible(item)"
            @remove="confirmRemoval(item)"
            @set-style="setStyle"
            @create-style="createStyle(item)"
            @toggle-3d="toggle3dView(item)"
            @zoom-to-layer-range="zoomToTheLayersRange(item)"
            @remove-style="removeStyle"
          >
          </layer>
        </b-list-group-item>
        <spinner class="layers-loading" :loading="layersLoading"></spinner>
      </b-list-group>
    </div>
    <b-btn-toolbar v-if="layers.length" class="layers-toolbar">
      <b-btn
        variant="primary"
        title="Cofnij zmiany"
        :disabled="!modified"
        @click="confirmRevert(composition)"
      >
        <icon name="undo" />
      </b-btn>
      <b-btn
        v-if="!readOnly"
        variant="primary"
        title="Zapisz kompozycję"
        :disabled="!modified"
        @click="saveComposition"
      >
        <icon name="save" />
      </b-btn>
      <span class="text-center version-info">Wersja: {{ version }}</span>
      <auth-b-btn
        variant="primary"
        title="Pobierz szablon"
        class="d-none d-lg-inline-block d-xl-inline-block"
        id="download-tool"
        v-if="getTemplateTool"
        @click="showDownloadTemplate()"
        :permissionId="'Templates_download'"
      >
        <icon name="download" />
      </auth-b-btn>
    </b-btn-toolbar>
    <confirmation-window ref="confirmationWindow"></confirmation-window>
    <save-composition-window></save-composition-window>
    <layer-style-window ref="styleWindow"></layer-style-window>
  </collapsible-widget>
</template>

<script>
import { mapGetters } from 'vuex';

import { LayerTypes } from '../common/layerEnums';
import MapLayerStyleBuilder from '../models/MapLayerStyleBuilder';
import MapLayerBuilder from '../models/MapLayerBuilder';
import Spinner from './Spinner';

import {
  WINDOW_SAVE_COMPOSITION_SHOW,
  WINDOW_DOWNLOAD_TEMPLATE_SHOW,
  WINDOW_NOTIFICATION_SHOW
} from '../store/mutations.type';
import {
  LAYERS_DELETE,
  STYLES_DELETE,
  TOGGLE_ACTIVE_LAYER,
  ACTIVATE_LAYER
} from '../store/actions.type';

import Layer from './Layer';
import CollapsibleWidget from './CollapsibleWidget';
import ConfirmationWindow from './ConfirmationWindow';
import SaveCompositionWindow from './SaveCompositionWindow';
import LayerStyleWindow from '../components/LayerStyleWindow';

export default {
  name: 'Layers',
  props: {
    accordion: {
      type: String,
      default: 'panel'
    },
    defaultExpanded: {
      type: Boolean,
      default: false
    },
    expandWidget: {
      type: Boolean,
      default: false
    }
  },
  components: {
    CollapsibleWidget,
    Layer,
    ConfirmationWindow,
    SaveCompositionWindow,
    LayerStyleWindow,
    Spinner
  },
  data: function() {
    return {
      initValuePrefix: 'init_',
      version: ''
    };
  },
  computed: {
    ...mapGetters([
      'readOnly',
      'isHistoryDataMode',
      'dataFeedNoGeometryLayerId',
      'layersLoading'
    ]),
    composition() {
      return this.$store.getters.composition || {};
    },
    layers() {
      return this.$store.getters.layers
        .filter(x => !x.hide)
        .sort((a, b) => {
          return b.order - a.order;
        });
    },
    ...mapGetters(['getTemplateTool', 'styles']),
    modified() {
      return this.layers.some(x => x.modified);
    },
    activated() {
      return this.layers.some(x => x.active);
    }
  },
  watch: {
    'composition.loaded': function(value) {
      if (!value) {
        this.revertModifications(true);
      }
    },
    dataFeedNoGeometryLayerId: function(value) {
      // tryb zasilenia bez geometrii - wymaga oznaczenia warstwy aktywnej
      // oraz aktywacji trybu edycji
      if (
        this.layers.length > 0 &&
        value != null &&
        this.layers.findIndex(x => x.id == value) != -1
      ) {
        this.activateLayer(this.layers.find(x => x.id == value));
      }
    }
  },
  methods: {
    onToggled(expanded) {
      this.$emit('toggled', expanded);
    },
    showConfirmation(message, yesAction, noAction) {
      let confirmationWindow = this.$refs.confirmationWindow;

      confirmationWindow.setMessage(message);
      confirmationWindow.setYesAction(yesAction);
      confirmationWindow.setNoAction(noAction);

      confirmationWindow.show();
    },
    confirmRevert(composition) {
      const self = this;

      self.showConfirmation(
        'Czy na pewno chcesz cofnąć wszystkie zmiany wprowadzone na liście warstw?',
        function() {
          self.revertModifications();
        }
      );
    },
    confirmRemoval(layer) {
      const self = this;

      self.showConfirmation(
        'Czy na pewno chcesz usunąć warstwę <b>' + layer.title + '</b>?',
        function() {
          self.$store.dispatch(LAYERS_DELETE, layer);
        }
      );
    },
    setStyle(layer, styleId) {
      this.setLayerProperty(layer, 'styleId', styleId);

      let style = this.styles.find(x => x.id === styleId);

      layer.mapLayer.setStyle(
        new MapLayerStyleBuilder(style, layer).getStyle()
      );

      new MapLayerBuilder(layer, style).updateOlLayer();

      layer.mapLayer.getSource().clear();
    },
    createStyle(layer) {
      this.$refs.styleWindow.setLayer(layer);
    },
    saveComposition() {
      this.$store.commit(WINDOW_SAVE_COMPOSITION_SHOW);
    },
    activateLayer(layer) {
      this.$store.dispatch(ACTIVATE_LAYER, layer);
    },
    isFirstLayer(layer) {
      let firstLayer = this.layers[0] || {};

      return layer.id === firstLayer.id;
    },
    isLastLayer(layer) {
      let lastLayer = this.layers[this.layers.length - 1] || {};

      return layer.id === lastLayer.id;
    },
    toggleActive(layer) {
      this.$store.dispatch(TOGGLE_ACTIVE_LAYER, { layer: layer });
    },
    toggleVisible(layer) {
      this.setLayerProperty(layer, 'visible', !layer.visible);
      layer.mapLayer.setVisible(layer.visible);

      if (layer.mapHighResolutionLayer) {
        layer.mapHighResolutionLayer.setVisible(layer.visible);
      }
    },
    toggle3dView(layer) {
      layer.view3d = !layer.view3d;

      new MapLayerBuilder(layer).updateOlLayer();
    },
    moveLayerUp(layer) {
      if (layer.id === this.layers[0].id) {
        return;
      }

      for (let i = this.layers.length - 1; i > 0; --i) {
        let current = this.layers[i];
        let previous = this.layers[i - 1];

        if (current.id === layer.id) {
          let currentOrder = current.order;
          this.setLayerProperty(current, 'order', previous.order);
          this.setLayerProperty(previous, 'order', currentOrder);

          break;
        }
      }

      this.applyLayersOrder();
    },
    moveLayerDown(layer) {
      if (layer.id === this.layers[this.layers.length - 1].id) {
        return;
      }

      for (let i = 0; i < this.layers.length - 1; ++i) {
        let current = this.layers[i];
        let next = this.layers[i + 1];

        if (current.id === layer.id) {
          let currentOrder = current.order;
          this.setLayerProperty(current, 'order', next.order);
          this.setLayerProperty(next, 'order', currentOrder);

          break;
        }
      }

      this.applyLayersOrder();
    },
    revertModifications(skipActive) {
      let self = this;

      this.layers.forEach(x => {
        let keys = Object.keys(x).filter(key => {
          return !key.startsWith(self.initValuePrefix);
        });

        for (let i = 0; i < keys.length; ++i) {
          let key = keys[i];
          let initialKey = self.initValuePrefix + key;

          if (key !== 'visible' || !skipActive || !x.active) {
            if (x[initialKey] !== undefined) {
              x[key] = x[initialKey];
              delete x[initialKey];
            }
          }
        }

        self.setMapLayerOrder(x);
        x.mapLayer.setVisible(x.visible);

        if (x.mapHighResolutionLayer) {
          x.mapHighResolutionLayer.setVisible(x.visible);
        }

        x.modified = false;
      });
    },
    setMapLayerOrder(layer) {
      layer.mapLayer.setZIndex(layer.order);

      if (layer.mapHighResolutionLayer) {
        layer.mapHighResolutionLayer.setZIndex(layer.order);
      }
    },
    applyLayersOrder() {
      const self = this;

      self.layers.forEach(x => {
        self.setMapLayerOrder(x);
      });

      var options = {
        message: 'Kolejność warstw została zmieniona pomyślnie.',
        title: 'Sukces.',
        type: 'success',
        duration: 5000
      };

      this.$store.commit(WINDOW_NOTIFICATION_SHOW, options);
    },
    showSuccessAlert() {
      var options = {
        message: 'Zmiany zostały zapisane.',
        title: 'Sukces.',
        type: 'success',
        duration: 5000
      };

      this.$store.commit(WINDOW_NOTIFICATION_SHOW, options);
    },
    setLayerProperty(layer, propertyName, value) {
      let self = this;
      let initialPropertyName = self.initValuePrefix + propertyName;

      if (layer[initialPropertyName] === undefined) {
        layer[initialPropertyName] = layer[propertyName];
      }

      layer[propertyName] = value;

      let keys = Object.keys(layer).filter(x => {
        return !x.startsWith(self.initValuePrefix);
      });

      layer.modified = keys.some(key => {
        let initialKey = self.initValuePrefix + key;

        return (
          layer[initialKey] !== undefined && layer[initialKey] !== layer[key]
        );
      });
    },
    showDownloadTemplate() {
      this.$store.commit(WINDOW_DOWNLOAD_TEMPLATE_SHOW);
    },
    zoomToTheLayersRange(layer) {
      this.$emit('set-layer-extent', layer.extent);
    },
    removeStyle(styleId) {
      this.$store.dispatch(STYLES_DELETE, styleId);
    }
  },
  created: function() {
    this.version = VERSION;
  }
};
</script>

<style>
#layers-list .dropdown > .btn {
  background: transparent;
  border: none;
  color: #007bff;
  padding: 3px;
  box-shadow: none;
}
#layers-container {
  position: relative;
}
.layers-toolbar {
  background-color: #fff;
  -webkit-flex: none;
  flex: none;
}
.layers-toolbar .btn {
  z-index: 10;
}
.layers-toolbar * {
  border-radius: 0 !important;
}
.layers-toolbar .btn {
  z-index: 10;
}

#download-tool {
  margin-left: auto;
}

.version-info {
  line-height: 40px;
  margin: auto;
}
.layers-loading {
  position: sticky;
  position: -webkit-sticky;
  bottom: 0;
  left: 50%;
}
</style>
